import { InfoIcon } from '@phosphor-icons/react'
import { Stack } from '@mui/material'
import { InfoBox, InfoText, Modal, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { type ExternalParticipant } from '@einwert/valuation-api-client'
import { useTranslation } from 'react-i18next'

type ExternalAppraiserDeleteModalProps = {
    isOpen: boolean
    onClose: () => void
    onDelete: () => void
    externalParticipant?: ExternalParticipant
    isPending: boolean
}

export function ExternalAppraiserDeleteModal({
    isOpen,
    onClose,
    onDelete,
    externalParticipant,
    isPending,
}: ExternalAppraiserDeleteModalProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderDetailsPage.externalAppraiser.delete' })
    const { t: tExternalAppraiser } = useTranslation('pages', { keyPrefix: 'orders.orderDetailsPage.externalAppraiser' })
    const { t: tCommonActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })

    if (!externalParticipant) {
        return null
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            showCloseIcon={!isPending}
            closeIconTooltip={tCommonActions('close')}
            showLoading={isPending}
            disableBackdropClose={isPending}
            width="580px"
            title={t('title')}
            footerProps={{
                primaryButton: {
                    text: tExternalAppraiser('remove'),
                    destructive: true,
                    disabled: isPending,
                    onClick: onDelete,
                },
                secondaryButton: {
                    text: tExternalAppraiser('cancel'),
                    disabled: isPending,
                    onClick: onClose,
                },
            }}
        >
            <Stack width="100%" gap={tokens.SizingSpacing400}>
                <InfoBox variant="external" icon={<InfoIcon />}>
                    <Stack gap={tokens.SizingSpacing100}>
                        <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                            {t('infoTitle')}
                        </Typography>
                        <Typography variant="textS" color={tokens.ColorTextBold}>
                            {t('infoText')}
                        </Typography>
                    </Stack>
                </InfoBox>

                <Stack>
                    <Typography variant="textS" color={tokens.ColorTextDefault} breakLongWords>
                        {t('futureEmailsStart')}{' '}
                        <Typography variant="textLinkS" color={tokens.ColorTextLinkDefault}>
                            {externalParticipant.email}
                        </Typography>{' '}
                        {t('futureEmailsEnd')}
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: tokens.SizingSpacing300 }}>
                        <li>
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {t('listImport')}
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {t('listVisible')}
                            </Typography>
                        </li>
                    </ul>
                </Stack>

                <InfoText variant="error" size="small" text={t('irreversible')} />
            </Stack>
        </Modal>
    )
}
